import React, { Component, Fragment } from 'react'
import { SpriteSheet3D } from 'aria-viewer/interactive-element/components/3d/spriteSheet3D/'
import { HitPlane3D } from 'aria-viewer/interactive-element/components/3d/hitMesh3D/'
import { RoundedRectangle3D } from 'aria-viewer/interactive-element/components/3d/customShapes3D/'

import PLAYER_BODY from 'public/assets/png/spriteAtlases/player_body.png'
import PLAYER_ICONS from 'public/assets/png/spriteAtlases/player_icons.png'
import { dispose3DObject, standardizeScale, disposeAnimations } from '../../../utils'

import {
    PositionalMediaButton3DAttributeValues,
    PositionalMediaButton3DSpeeds,
} from 'aria-viewer/interactive-element/components/3d/positionalMedia3D/constants/'

// Wrapper component that defines callbacks to manipulate attributes on callbacks to drive animations
// on wrapped 3d mesh component

class PositionalMediaButton3D extends Component {
    constructor(props) {
        super(props)
        this.state = {
            currentVideoState: props.autoplay ? 'playing' : 'paused',
            rootObj: null,
        }
        this.actions = {
            icon: {},
            iconBlur: {},
        }
        this.clips = {}
        this.mixers = {}
        this.clock = new THREE.Clock()
        this.iconObj
        this.iconBlurObj
        this.bodyBlurObj
        this.bodyFillObj
        this.bodyBorderObj
        this.hideButtonTimeout
        this.allowAnimations = true
    }

    componentDidMount() {
        const { scene, meshPosition, rootObj, camera, renderer, height, viewportWidth, useResponsiveUI } = this.props
        const { currentVideoState } = this.state

        let _rootObj = new THREE.Object3D()
        _rootObj.name = 'buttonRoot'
        _rootObj.position.set(meshPosition.x, meshPosition.y, meshPosition.z)
        this.setState({ rootObj: _rootObj })
        this.addScrubBar(_rootObj)
        let targetScale = useResponsiveUI ? viewportWidth / 7 : 150
        _rootObj = standardizeScale(_rootObj, camera, renderer, height, targetScale, 0.6)
        _rootObj.lookAt(new THREE.Vector3(0, 0, 0))
        if (rootObj) {
            rootObj.add(_rootObj)
        } else {
            scene.add(_rootObj)
        }

        // Gives webgl children time to mount
        this.webGlAnimationsTimeout = setTimeout(() => {
            _rootObj && this.createWebGlAnimations(_rootObj)
        }, 1000)

        // Sets timeout if PM autoplays
        if (currentVideoState === 'playing') {
            this.hideButtonTimeout = setTimeout(() => {
                _rootObj.visible = false
            }, PositionalMediaButton3DSpeeds.PM_HIDEBUTTON_TIMEOUT_DURATION)
        }

        this.animateButton()
    }

    componentDidUpdate(prevProps) {
        // Note that props.state refer to what the button should show rather than what the video is doing
        // So the state is 'play' when the video is paused because the video is paused
        // I found this infuriating so I reversed it in the state.  The currentVideoState refers to the state of the video
        const { rootObj, currentVideoState } = this.state
        // Makes videoState tether to props coming in from PM slice so that clicks on other PM will trigger changes
        if (currentVideoState !== 'repeat' && this.props.progress === 1) {
            // Show repeat when finished
            clearTimeout(this.hideButtonTimeout)
            this.setState({ currentVideoState: 'repeat' })
            rootObj.visible = true
        } else if (currentVideoState === 'repeat' && this.props.progress !== 1) {
            // Show play state when restarting
            this.setState({ currentVideoState: 'playing' })
            // Make sure button hide timeout is triggered
            this.hideButtonTimeout = setTimeout(() => {
                rootObj.visible = false
            }, PositionalMediaButton3DSpeeds.PM_HIDEBUTTON_TIMEOUT_DURATION)
        } else if (prevProps.state === 'play' && this.props.state !== 'play') {
            // Show play state when initiating
            this.setState({ currentVideoState: 'playing' })
            // Make sure button hide timeout is triggered
            this.hideButtonTimeout = setTimeout(() => {
                rootObj.visible = false
            }, PositionalMediaButton3DSpeeds.PM_HIDEBUTTON_TIMEOUT_DURATION)
        } else if (prevProps.state !== 'play' && this.props.state === 'play') {
            // Show paused state when paused
            rootObj.visible = true
            clearTimeout(this.hideButtonTimeout)
            this.setState({ currentVideoState: 'paused' })
        }
    }

    createWebGlAnimations = obj => {
        this.iconObj = obj.getObjectByName('icon', true)
        this.iconBlurObj = obj.getObjectByName('iconBlur', true)
        this.bodyBlurObj = obj.getObjectByName('bodyBlur', true)
        this.bodyFillObj = obj.getObjectByName('bodyFill', true)
        this.bodyBorderObj = obj.getObjectByName('bodyBorder', true)

        //     // https://threejs.org/docs/#api/en/animation/AnimationMixer
        //     // "The AnimationMixer is a player for animations on a particular object in the scene.
        //     //  When multiple objects in the scene are animated independently, one AnimationMixer may be used for each object."
        if (obj && this.iconObj && this.iconBlurObj) {
            this.mixers.icon = new THREE.AnimationMixer(this.iconObj)
            this.mixers.iconBlur = new THREE.AnimationMixer(this.iconBlurObj)

            //     // SPRITE ANIMS

            //     // Animation for slightly increasing scale on hover
            var hoverEnterScaleKF = new THREE.VectorKeyframeTrack(
                '.scale',
                PositionalMediaButton3DSpeeds.PM_HOVERENTER_ICON_SCALE_ANIM_TIMING,
                PositionalMediaButton3DAttributeValues.PM_HOVERENTER_ICON_SCALE_ANIM_KEYFRAME_SEQUENCE
            )
            this.clips.hoverEnterScaleClip = new THREE.AnimationClip(
                'spriteHoverEnterScale',
                PositionalMediaButton3DSpeeds.PM_HOVERENTER_ICON_SCALE_ANIM_DURATION,
                [hoverEnterScaleKF]
            )

            this.actions.icon.hoverEnterScaleClip = this.mixers.icon.clipAction(this.clips.hoverEnterScaleClip)
            this.actions.icon.hoverEnterScaleClip.loop = THREE.LoopOnce
            this.actions.icon.hoverEnterScaleClip.clampWhenFinished = true

            this.actions.iconBlur.hoverEnterScaleClip = this.mixers.iconBlur.clipAction(this.clips.hoverEnterScaleClip)
            this.actions.iconBlur.hoverEnterScaleClip.loop = THREE.LoopOnce
            this.actions.iconBlur.hoverEnterScaleClip.clampWhenFinished = true

            //     // Animation for slightly decreasing scale on exit
            var hoverExitScaleKF = new THREE.VectorKeyframeTrack(
                '.scale',
                PositionalMediaButton3DSpeeds.PM_HOVEREXIT_ICON_SCALE_ANIM_TIMING,
                PositionalMediaButton3DAttributeValues.PM_HOVEREXIT_ICON_SCALE_ANIM_KEYFRAME_SEQUENCE
            )
            this.clips.hoverExitScaleClip = new THREE.AnimationClip(
                'spriteHoverExitScale',
                PositionalMediaButton3DSpeeds.PM_HOVEREXIT_ICON_SCALE_ANIM_DURATION,
                [hoverExitScaleKF]
            )

            this.actions.icon.hoverExitScaleClip = this.mixers.icon.clipAction(this.clips.hoverExitScaleClip)
            this.actions.icon.hoverExitScaleClip.loop = THREE.LoopOnce
            this.actions.icon.hoverExitScaleClip.clampWhenFinished = true

            this.actions.iconBlur.hoverExitScaleClip = this.mixers.iconBlur.clipAction(this.clips.hoverExitScaleClip)
            this.actions.iconBlur.hoverExitScaleClip.loop = THREE.LoopOnce
            this.actions.iconBlur.hoverExitScaleClip.clampWhenFinished = true
        }

        this.animateMixers()
    }

    handleMouseOver = () => {
        const { rootObj } = this.state
        const { onHover, setCursor } = this.props
        // // wire this up to setCursor in container component so that cursor style chamnges
        setCursor && setCursor('pointer')

        clearTimeout(this.hideButtonTimeout)
        rootObj.visible = true

        this.actions.icon.hoverEnterScaleClip && this.actions.icon.hoverEnterScaleClip.play()
        this.actions.icon.hoverExitScaleClip && this.actions.icon.hoverExitScaleClip.stop()

        onHover && onHover()
    }

    handleTouchStart = () => {}

    handleMouseOut = () => {
        const { rootObj, currentVideoState } = this.state
        const { onHoverExit, setCursor, isMobile } = this.props

        this.actions.icon.hoverExitScaleClip && this.actions.icon.hoverExitScaleClip.play()
        this.actions.icon.hoverEnterScaleClip && this.actions.icon.hoverEnterScaleClip.stop()

        if (currentVideoState === 'playing') {
            this.hideButtonTimeout = setTimeout(() => {
                rootObj.visible = false
            }, PositionalMediaButton3DSpeeds.PM_HIDEBUTTON_TIMEOUT_DURATION)
        }

        // // wire this up to setCursor in container component so that cursor style chamnges
        setCursor && setCursor('grab')
        onHoverExit && onHoverExit()
    }

    handleClick = () => {
        const { currentVideoState, rootObj } = this.state
        const { isMobile, onClick } = this.props
        // Hide positional media button while playing if on mobile
        if (isMobile) {
            // Checking for prior state, i.e., show if prior state was playing, hide if prior state was paused
            rootObj.visible = currentVideoState === 'playing'
        }

        onClick && onClick()
    }

    animateMixers = () => {
        if (!this.allowAnimations) return
        var delta = this.clock.getDelta()
        this.mixers &&
            Object.keys(this.mixers).forEach(mixerKey => {
                this.mixers[mixerKey].update(delta)
            })
        this.raf = requestAnimationFrame(this.animateMixers)
    }

    componentWillUnmount() {
        this.allowAnimations = false

        disposeAnimations({
            mixers: this.mixers,
            actions: this.actions,
            clips: this.clips,
        })
        dispose3DObject(this.state.rootObj)

        this.raf && cancelAnimationFrame(this.raf)
        this.rafMesh && cancelAnimationFrame(this.rafMesh)
        clearTimeout(this.webGlAnimationsTimeout)
        clearTimeout(this.hideButtonTimeout)
        this.raf = null
        this.rafMesh = null
        this.clock = null
        this.setState({ rootObj: null })
    }

    addScrubBar = parentObj => {
        const {
            scrubBarColor,
            height,
            width,
            scrubBarGeometryWidthCofactor,
            scrubBarGeometryHeightCofactor,
            scrubBarGeometryEdgeRadius,
            scrubBarPositionXCofactor,
            scrubBarPositionYCofactor,
            scrubBarPositionZ,
        } = this.props
        var geometry = RoundedRectangle3D(
            0,
            0,
            width * scrubBarGeometryWidthCofactor,
            height * scrubBarGeometryHeightCofactor,
            scrubBarGeometryEdgeRadius
        )
        var material = new THREE.MeshBasicMaterial({ color: scrubBarColor, side: THREE.DoubleSide })
        this.scrubBar = new THREE.Mesh(geometry, material)
        // Scrubbar z position is hardcoded to put it in front of other positional media button spritesheets
        this.scrubBar.position.set(
            width * scrubBarPositionXCofactor,
            height * scrubBarPositionYCofactor,
            scrubBarPositionZ
        )
        this.scrubBar.name = 'scrubBar'
        parentObj.add(this.scrubBar)
    }

    animateButton = () => {
        if (!this.allowAnimations) return
        const { progress, height, width } = this.props
        // Rotate circle to face camera but don't rotate sprite
        // Keeps margin of circle constant wrt sprite image
        if (progress <= 1) {
            this.scrubBar && this.scrubBar.scale.set(progress, this.scrubBar.scale.y, this.scrubBar.scale.z)
            // Offset position to adjust for centered scaling
            // https://stackoverflow.com/questions/33454919/scaling-a-three-js-geometry-only-up
            this.scrubBar &&
                this.scrubBar.position.set(-width / 2 + 0.08, this.scrubBar.position.y, this.scrubBar.position.z)
        }

        this.rafMesh = requestAnimationFrame(this.animateButton)
    }

    render() {
        const { rootObj, currentVideoState } = this.state
        const {
            position,
            currentState,
            isVisible,
            meshPosition,
            progress,
            handleTouchStart,
            handleMouseOut,
            handleMouseOver,
            hitMeshOffset,
            isMobile,
            ...props
        } = this.props
        // The depth offset is used to avoid z-fighting in the composed button.  Maybe swap out for render order?  Will that allow for transparency?
        // Only needs to be ~0.01 to avoid z-fighting between spritesheets
        // 0.5 of depthoffset value is to avoid z-fighting with actual positional media.  Not necessary for all buttons, but some are placed too close to the button
        return (
            rootObj && (
                <Fragment>
                    <HitPlane3D
                        opacity={0}
                        rootObj={rootObj}
                        handleClick={this.handleClick}
                        handleTouchStart={this.handleClick}
                        handleMouseOut={this.handleMouseOut}
                        handleMouseOver={this.handleMouseOver}
                        isMobile={isMobile}
                        // Needed to avoid z-fighting in certain cases
                        depthOffset={hitMeshOffset}
                        width={1}
                        height={1}
                        camera={props.camera}
                        renderer={props.renderer}
                        scene={props.scene}
                        // {...props}
                    />
                    <Fragment>
                        {/* <SpriteSheet3D
                            depthOffset={0.51}
                            spriteName={'bodyBlur'}
                            spriteResource={PLAYER_BODY}
                            numberOfStates={4}
                            currentState={'persistent'}
                            rootObj={rootObj}
                            stateIndexMap={{
                                persistent: 1,
                            }}
                            {...props}
                        /> */}
                        <SpriteSheet3D
                            depthOffset={0.515}
                            spriteName={'bodyFill'}
                            spriteResource={PLAYER_BODY}
                            numberOfStates={4}
                            currentState={'persistent'}
                            rootObj={rootObj}
                            stateIndexMap={{
                                persistent: 3,
                            }}
                            {...props}
                        />
                        <SpriteSheet3D
                            depthOffset={0.52}
                            spriteName={'bodyBorder'}
                            spriteResource={PLAYER_BODY}
                            numberOfStates={4}
                            currentState={'persistent'}
                            rootObj={rootObj}
                            stateIndexMap={{
                                persistent: 0,
                            }}
                            {...props}
                        />
                    </Fragment>
                    <Fragment>
                        <SpriteSheet3D
                            depthOffset={0.55}
                            spriteName={'icon'}
                            spriteResource={PLAYER_ICONS}
                            numberOfStates={6}
                            currentState={currentVideoState}
                            rootObj={rootObj}
                            stateIndexMap={{
                                paused: 1,
                                playing: 3,
                                repeat: 5,
                            }}
                            {...props}
                        />
                    </Fragment>
                </Fragment>
            )
        )
    }
}

PositionalMediaButton3D.defaultProps = {
    scrubBarGeometryWidthCofactor: PositionalMediaButton3DAttributeValues.SCRUBBAR_GEOMETRY_WIDTH_COFACTOR,
    scrubBarGeometryHeightCofactor: PositionalMediaButton3DAttributeValues.SCRUBBAR_GEOMETRY_HEIGHT_COFACTOR,
    scrubBarGeometryEdgeRadius: PositionalMediaButton3DAttributeValues.SCRUBBAR_GEOMETRY_EDGE_RADIUS,
    scrubBarPositionXCofactor: PositionalMediaButton3DAttributeValues.SCRUBBAR_POSITION_X_COFACTOR,
    scrubBarPositionYCofactor: PositionalMediaButton3DAttributeValues.SCRUBBAR_POSITION_Y_COFACTOR,
    scrubBarPositionZ: PositionalMediaButton3DAttributeValues.SCRUBBAR_POSITION_Z,
    hitMeshOffset: PositionalMediaButton3DAttributeValues.HIT_MESH_OFFSET,
    meshPosition: PositionalMediaButton3DAttributeValues.MESH_POSITION,
    width: PositionalMediaButton3DAttributeValues.WIDTH,
    height: PositionalMediaButton3DAttributeValues.HEIGHT,
    scrubBarColor: PositionalMediaButton3DAttributeValues.SCRUB_BAR_COLOR,
    progress: 0,
}

export default PositionalMediaButton3D
